import { bunja } from 'bunja';
import { atom } from 'jotai';

import { symbolFavoritesBunja } from '@flipster/features/src/shared/favorite-symbol/state';
import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { marketsBunja as perpMarketsBunja } from '@flipster/r230522/original/market';
import { spotMarketsBunja } from '@flipster/r230522/original/spot-market';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { instrumentFuzzySearch } from '@flipster/utils/fuzzy';

import { marketPageBunja, marketSymbolBunja } from '../../state';
import {
  filterFavoriteSymbols,
  getDiscoverTabs,
  resolveCandidateSymbols,
  resolveSearchPool,
  sortTableItems,
  toTableItems,
} from '../../utils';

const marketsBunja = bunja(() => {
  const { marketsAtom: perpMarketsAtom } = bunja.use(perpMarketsBunja);
  const { spotMarketsAtom } = bunja.use(spotMarketsBunja);

  const marketsAtom = atom((get) => ({
    ...get(spotMarketsAtom),
    ...get(perpMarketsAtom),
  }));

  return { marketsAtom };
});
marketsBunja.debugLabel = 'marketsBunja';

export const marketTableBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { tab, pageAtom, searchCursorAtom, searchTabAtom, discoverCursorAtom, discoverTradfiCategoryAtom } =
    bunja.use(marketPageBunja);
  const { symbolsByMarketTabAtom } = bunja.use(marketSymbolBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const { favoritesAtom } = bunja.use(symbolFavoritesBunja);
  const { marketsAtom } = bunja.use(marketsBunja);

  const favoriteSymbolsAtom = atom((get) =>
    filterFavoriteSymbols([...get(favoritesAtom)], store.get(instrumentsKeyBySymbolAtom), tab === 'spot'),
  );
  const dataRefByTabAtom = atom((get) => {
    const symbols = get(symbolsByMarketTabAtom);
    return new Map(getDiscoverTabs(symbols?.layout?.components).map((t) => [t.value, t.dataRef]));
  });

  // step 1-1. get symbols by search
  const searchSymbolsAtom = atom((get) => {
    const search = get(searchCursorAtom).query;
    const searchTab = get(searchTabAtom);
    if (!search.trim()) return;
    const symbols = get(symbolsByMarketTabAtom);
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    if (!symbols) return;
    return instrumentFuzzySearch(search, resolveSearchPool(searchTab, symbols), instrumentsKeyBySymbol);
  });
  const searchItemsAtom = atom((get) => {
    const searchSymbols = get(searchSymbolsAtom);
    if (!searchSymbols) return;
    return toTableItems(get(marketsAtom), searchSymbols, store.get(instrumentsKeyBySymbolAtom));
  });

  // step 1-2. get symbols by discover tab and sort it
  const candidateSymbolsByTabAtom = atom((get) => {
    const discoverTab = get(discoverCursorAtom).tab;
    return resolveCandidateSymbols({
      tab: discoverTab,
      favoriteSymbols: get(favoriteSymbolsAtom),
      tradfiCategory: get(discoverTradfiCategoryAtom),
      dataRef: get(dataRefByTabAtom).get(discoverTab),
      symbols: get(symbolsByMarketTabAtom),
    });
  });
  const candidateItemsByTabAtom = atom((get) => {
    const candidateSymbolsByTab = get(candidateSymbolsByTabAtom);
    if (!candidateSymbolsByTab) return;
    return toTableItems(get(marketsAtom), candidateSymbolsByTab, store.get(instrumentsKeyBySymbolAtom));
  });
  const sortedItemsAtom = atom((get) =>
    sortTableItems(get(candidateItemsByTabAtom), get(discoverCursorAtom).sort, get(instrumentsKeyBySymbolAtom)),
  );

  // step 2. select proper final items by current mode
  const itemsAtom = atom((get) => {
    const isSearch = get(searchCursorAtom).query.trim() != '';
    return isSearch ? get(searchItemsAtom) : get(sortedItemsAtom);
  });

  const paginationAtom = atom((get) => ({ page: get(pageAtom), per: 30, total: get(itemsAtom)?.length ?? 0 }));
  const paginatedItemsAtom = atom((get) => {
    const items = get(itemsAtom);
    const { page, per } = get(paginationAtom);
    return items?.slice(page * per, (page + 1) * per);
  });

  return { itemsAtom, paginationAtom, paginatedItemsAtom };
});
marketTableBunja.debugLabel = 'marketTableBunja';
